import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Component, inject, OnInit, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { faCalendarAlt, faPlus } from '@fortawesome/free-solid-svg-icons';
import { AccountService } from '@infodocs/shared/services/account.service';
import { CipcCustomerDefault } from '@infodocs/account/variables/enums';
import { CipcService } from '@infodocs/cipc/cipc.service';
import { NameReservation } from '@infodocs/company/interfaces/reservation';
import { CipcTypeId } from '@infodocs/company/variables/enums';
import { SubmitButtonComponent } from '@infodocs/shared/components/submit-button/submit-button.component';
import { ComponentLoaderComponent } from '@infodocs/shared/components/component-loader/component-loader.component';
import { StepComponent } from '@infodocs/shared/components/step/step.component';
import { StepperComponent } from '@infodocs/shared/components/stepper/stepper.component';
import { CheckoutFormComponent } from '@infodocs/shared/forms/checkout-form/checkout-form.component';
import { PortfolioCompanyDetailsFormComponent } from '@infodocs/portfolio/modals/add-company-modal/portfolio-company-details-form/portfolio-company-details-form.component';
import { CompanyInformationConfirmationComponent } from '@infodocs/portfolio/modals/add-company-modal/company-information-confirmation/company-information-confirmation.component';
import { DirectorsInformationComponent } from '@infodocs/portfolio/modals/add-company-modal/directors-information/directors-information.component';
import RegistersCompanyComponent from '@infodocs/portfolio/modals/add-company-modal/registers-company.component';
import {
	ProposedNamesFormComponent,
	ProposedNamesResult,
} from '@infodocs/portfolio/modals/add-company-modal/name-reservation/proposed-names-form/proposed-names-form.component';
import { CheckoutForm, CheckoutItem } from '@infodocs/shared/interfaces/checkout';
import { SuccessResponse } from '@infodocs/shared/interfaces/success-response';
import { IconComponent } from '@infodocs/shared/components/icon/icon.component';
import { companyNameValidator, strictCipcNameValidator } from '@infodocs/shared/forms/validators/custom-validators';
import { infodocsDateToString } from '@infodocs/shared/utils/dates';
import { getFirstApiErrorMessage } from '@infodocs/shared/utils/company-search-errors';
import { AlertModule } from 'ngx-bootstrap/alert';
import { firstValueFrom, from, takeUntil, tap } from 'rxjs';

const CALENDAR_EVENT_TITLE = 'Submit Company Registration Documents';
const CALENDAR_EVENT_DESCRIPTION = `This is your automated reminder.

Please email the supporting documents to eServicesCOREG@cipc.co.za with only the tracking number as the subject (this should have been sent to you):
• Verified/Certified ID for each director
• Signed Company Registration (COREG)

Documents must be submitted within 10 calendar days to avoid expiry.

For feedback or support, contact us at help.infodocs.co.za`;

const RESERVE_NEW_VALUE = 0;

type NameStepState = 'select' | 'reserveNew' | 'capturingProposedNames';

function dateInDays(days: number): Date {
	const d = new Date();
	d.setDate(d.getDate() + days);
	return d;
}

function toYyyyMmDd(date: Date): string {
	return `${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, '0')}${String(date.getDate()).padStart(2, '0')}`;
}

export enum NameReservationStage {
	NOTICE,
	NAME_RESERVATION,
	COMPANY_DETAILS,
	DIRECTORS_INFORMATION,
	COMPANY_INFORMATION_CONFIRMATION,
	CHECKOUT,
	SUCCESS,
}

@Component({
	selector: 'app-name-reservation',
	imports: [
		StepperComponent,
		StepComponent,
		IconComponent,
		ReactiveFormsModule,
		SubmitButtonComponent,
		PortfolioCompanyDetailsFormComponent,
		DirectorsInformationComponent,
		CompanyInformationConfirmationComponent,
		ComponentLoaderComponent,
		CheckoutFormComponent,
		ProposedNamesFormComponent,
		AlertModule,
	],
	templateUrl: './name-reservation.component.html',
	styleUrl: './name-reservation.component.scss',
})
export class NameReservationComponent extends RegistersCompanyComponent implements OnInit {
	protected readonly fb = inject(FormBuilder);
	protected readonly faPlusIcon = faPlus;
	protected readonly faCalendarIcon = faCalendarAlt;
	private readonly http = inject(HttpClient);

	protected get googleCalendarUrl(): string {
		const dates = `${toYyyyMmDd(dateInDays(10))}/${toYyyyMmDd(dateInDays(11))}`;
		return `https://calendar.google.com/calendar/render?${new URLSearchParams({
			action: 'TEMPLATE',
			text: CALENDAR_EVENT_TITLE,
			dates,
			details: CALENDAR_EVENT_DESCRIPTION,
			location: 'Online',
		})}`;
	}

	protected readonly nameReservationForm = this.fb.group({
		name_reservation_id: ['', Validators.required],
		id: [''],
		name_1: ['', Validators.required],
		name_2: ['', [companyNameValidator(), strictCipcNameValidator()]],
		name_3: ['', [companyNameValidator(), strictCipcNameValidator()]],
		name_4: ['', [companyNameValidator(), strictCipcNameValidator()]],
		tracking_no: [''],
	});
	protected readonly transactionFound = signal<boolean>(false);
	protected readonly transactionResponse = signal<SuccessResponse | undefined>(undefined);
	protected readonly items = signal<CheckoutItem[]>([]);
	protected readonly nameReservationOptions = signal<NameReservation[]>([]);
	protected readonly nameStepState = signal<NameStepState>('select');
	protected readonly proposedNamesDraft = signal<Partial<ProposedNamesResult>>({});
	protected readonly cipcTypeId = CipcTypeId.new_company_registration;
	protected readonly cipcService = inject(CipcService);
	private readonly accountService = inject(AccountService);

	public ngOnInit() {
		this.cipcService.clearTransaction();
		this.subscribeToExistingReservationSelection();
		this.loadNameReservationOptions();
		this.getCipcTransaction();
	}

	protected displayCheckout() {
		return from(this.cipcService.isPaid())
			.pipe(takeUntil(this.destroy$))
			.subscribe(isPaid => {
				if (isPaid) {
					return this.displaySuccess();
				}

				this.setCheckoutItems();

				return this.stepper()?.selectStep(NameReservationStage.CHECKOUT);
			});
	}

	protected displaySuccess() {
		return this.cipcService
			.submitToCipc(this.cipcService.transaction)
			.pipe(
				tap(() => this.stepper()?.selectStep(NameReservationStage.SUCCESS)),
				takeUntil(this.destroy$)
			)
			.subscribe({
				next: result => this.transactionResponse.set(result),
				error: (err: HttpErrorResponse) => this.errorMessage.set(err.error.message || 'Error submitting to CIPC'),
			});
	}

	protected submitPayment(checkout: CheckoutForm) {
		return this.cipcService
			.submitPayment(this.cipcService.transaction, checkout)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: () => {
					void this.confirmPaymentThenSubmit();
				},
				error: (err: HttpErrorResponse) => this.errorMessage.set(err.error.message || 'Error submitting payment'),
			});
	}

	private async confirmPaymentThenSubmit(): Promise<void> {
		const paid = await this.waitForPaymentConfirmation();
		if (!paid) {
			this.errorMessage.set(
				'Payment is still processing. Please wait a moment and try Confirm again, or contact support if this persists.'
			);
			return;
		}

		this.displaySuccess();
	}

	private async waitForPaymentConfirmation(attempts = 10, delayMs = 500): Promise<boolean> {
		for (let attempt = 0; attempt < attempts; attempt++) {
			if (await this.cipcService.isPaid()) {
				return true;
			}

			if (attempt < attempts - 1) {
				await new Promise(resolve => setTimeout(resolve, delayMs));
			}
		}

		return false;
	}

	protected override companyRegistrationDetails() {
		const directors = this.directorsInformationComponent()?.getDirectorsDetails() ?? [];
		const directorsForCoreg = directors.map(d => ({
			...d,
			date_of_birth: d.date_of_birth && !d.citizen
				? infodocsDateToString(d.date_of_birth, '/')
				: d.date_of_birth,
		}));
		const nameReservation = this.nameReservationForm.value;
		const reserveNew = this.isReserveNewSelected();

		return {
			company_id: null,
			cipc_type_id: CipcTypeId.new_company_registration,
			name_reservation: {
				id: reserveNew ? undefined : nameReservation.id || undefined,
				name: nameReservation.name_1,
				name_1: nameReservation.name_1,
				name_2: reserveNew ? nameReservation.name_2 || undefined : undefined,
				name_3: reserveNew ? nameReservation.name_3 || undefined : undefined,
				name_4: reserveNew ? nameReservation.name_4 || undefined : undefined,
				tracking_no: reserveNew ? undefined : nameReservation.tracking_no || undefined,
			},
			company: this.companyDetailsFormComponent()?.getCompanyDetails(),
			directors: directorsForCoreg,
			customer_code: this.accountService.currentAccount()?.customer_code || CipcCustomerDefault.code,
		};
	}

	protected isCompanyFormsValid(): boolean {
		return (this.companyDetailsFormComponent()?.companyForms() ?? []).filter(Boolean).every(control => control!.valid);
	}

	protected areDirectorsFormsValid(): boolean {
		return (this.directorsInformationComponent()?.directorForms() ?? [])
			.filter(Boolean)
			.every(control => control!.valid);
	}

	protected submitInformationToCipc() {
		if (!this.stepper()?.canScrollToAnotherStep()) {
			return;
		}

		this.errorMessage.set(undefined);
		this.cipcService
			.submitInformation(this.cipcService.transaction, this.companyRegistrationDetails())
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: () => this.stepper()?.selectStep(NameReservationStage.COMPANY_INFORMATION_CONFIRMATION),
				error: err =>
					this.errorMessage.set(
						getFirstApiErrorMessage(err, 'Error submitting information')
					),
			});
	}

	private getCipcTransaction() {
		this.cipcService.transaction.cipc_type_id = CipcTypeId.new_company_registration;
		this.cipcService.transaction.company_id = this.companyService.currentCompany()?.id;

		this.cipcService
			.fetchOrCreateTransaction(this.cipcService.transaction)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: () => this.transactionFound.set(true),
				error: error => this.errorMessage.set(error.error.message || 'Failed to fetch or create transaction'),
			});
	}

	private loadNameReservationOptions() {
		firstValueFrom(this.http.get<NameReservation[]>('/api/account/namereservation'))
			.then(result => {
				this.nameReservationOptions.set(result);
			})
			.catch(() => {
				this.nameReservationOptions.set([]);
			});
	}

	protected openProposedNamesForm(): void {
		const current = this.nameReservationForm.getRawValue();
		this.proposedNamesDraft.set({
			name_1: current.name_1 || '',
			name_2: current.name_2 || '',
			name_3: current.name_3 || '',
			name_4: current.name_4 || '',
		});
		this.nameStepState.set('capturingProposedNames');
	}

	protected onProposedNamesConfirmed(names: ProposedNamesResult): void {
		this.applyProposedNames(names);
		this.stepper()?.selectStep(NameReservationStage.COMPANY_DETAILS);
	}

	protected onProposedNamesCancelled(): void {
		this.cancelProposedNames();
	}

	private subscribeToExistingReservationSelection() {
		this.nameReservationForm
			.get('name_reservation_id')
			?.valueChanges.pipe(takeUntil(this.destroy$))
			.subscribe((value: string | null) => {
				if (value === null || value === '') {
					return;
				}

				if (+value === RESERVE_NEW_VALUE) {
					// Ignore programmatic patches after proposed names are confirmed.
					if (this.nameStepState() === 'reserveNew' && this.nameReservationForm.value.name_1) {
						return;
					}
					if (this.nameStepState() === 'capturingProposedNames') {
						return;
					}

					this.prepareReserveNewSelection();
					this.openProposedNamesForm();
					return;
				}

				const selected = this.nameReservationOptions().find(option => option.id === +value);
				if (!selected) {
					return;
				}

				this.nameStepState.set('select');
				this.nameReservationForm.controls.id.setValidators([Validators.required]);
				this.nameReservationForm.controls.tracking_no.setValidators([Validators.required]);
				this.nameReservationForm.controls.name_1.setValidators([Validators.required]);
				this.nameReservationForm.patchValue({
					id: String(selected.id),
					name_1: selected.name,
					name_2: '',
					name_3: '',
					name_4: '',
					tracking_no: selected.tracking_no,
				});
				this.nameReservationForm.controls.name_1.updateValueAndValidity();
				this.nameReservationForm.controls.id.updateValueAndValidity();
				this.nameReservationForm.controls.tracking_no.updateValueAndValidity();
			});
	}

	private prepareReserveNewSelection(): void {
		this.nameStepState.set('reserveNew');
		this.nameReservationForm.patchValue({
			id: '',
			tracking_no: '',
			name_1: '',
			name_2: '',
			name_3: '',
			name_4: '',
		});
		this.nameReservationForm.controls.id.clearValidators();
		this.nameReservationForm.controls.tracking_no.clearValidators();
		this.nameReservationForm.controls.name_1.setValidators([
			Validators.required,
			companyNameValidator(),
			strictCipcNameValidator(),
		]);
		this.nameReservationForm.controls.name_1.updateValueAndValidity();
		this.nameReservationForm.controls.id.updateValueAndValidity();
		this.nameReservationForm.controls.tracking_no.updateValueAndValidity();
	}

	private applyProposedNames(names: ProposedNamesResult): void {
		this.nameStepState.set('reserveNew');
		this.nameReservationForm.patchValue(
			{
				name_reservation_id: String(RESERVE_NEW_VALUE),
				id: '',
				tracking_no: '',
				...names,
			},
			{ emitEvent: false }
		);
		this.nameReservationForm.controls.id.clearValidators();
		this.nameReservationForm.controls.tracking_no.clearValidators();
		this.nameReservationForm.controls.name_1.setValidators([
			Validators.required,
			companyNameValidator(),
			strictCipcNameValidator(),
		]);
		this.nameReservationForm.controls.name_1.updateValueAndValidity();
		this.nameReservationForm.controls.id.updateValueAndValidity();
		this.nameReservationForm.controls.tracking_no.updateValueAndValidity();
	}

	private cancelProposedNames(): void {
		if (this.nameReservationForm.value.name_1) {
			this.nameStepState.set('reserveNew');
			return;
		}

		this.nameStepState.set('select');
		this.nameReservationForm.patchValue({
			name_reservation_id: '',
			id: '',
			tracking_no: '',
			name_1: '',
			name_2: '',
			name_3: '',
			name_4: '',
		});
	}

	private setCheckoutItems(): void {
		const checkoutItems: CheckoutItem[] = [
			{
				name: 'Company Registration',
				filter: 'coreg',
				data: this.nameReservationForm.value,
			},
		];

		if (this.isReserveNewSelected()) {
			checkoutItems.unshift({
				name: 'Name Reservation',
				filter: 'name-reservation',
				data: this.nameReservationForm.value,
			});
		}

		this.items.set(checkoutItems);
	}

	protected isReserveNewSelected(): boolean {
		return Number(this.nameReservationForm.value.name_reservation_id) === RESERVE_NEW_VALUE;
	}
}
